// Interaction focus: the highest-priority, then nearest, enabled interactable in
// range of the player gets an in-world prompt; E triggers it, X its alt action.

import Phaser from 'phaser';
import { DEPTH, FONT } from '../../config/constants';
import { COLORS, css } from '../../config/palette';
import type { GameContext, InteractApi, Interactable } from '../../core/contracts';

export function createInteract(ctx: GameContext): InteractApi {
  const scene = ctx.scene;
  const items = new Set<Interactable>();
  let focused: Interactable | null = null;
  let label: Phaser.GameObjects.Text | null = null;

  const ensureLabel = (): Phaser.GameObjects.Text => {
    if (!label || !label.scene) {
      label = scene.add
        .text(0, 0, '', {
          fontFamily: FONT.display,
          fontSize: '22px',
          fontStyle: '600',
          color: css(COLORS.uiText),
          backgroundColor: 'rgba(10,14,20,0.82)',
          padding: { x: 10, y: 5 },
          align: 'center',
        })
        .setOrigin(0.5, 1)
        .setDepth(DEPTH.WORLD_UI)
        .setAlpha(0);
    }
    return label;
  };

  const pick = (): Interactable | null => {
    const p = ctx.player;
    if (!p || !p.alive) return null;
    let best: Interactable | null = null;
    let bestPri = -Infinity;
    let bestDist = Infinity;
    for (const it of items) {
      if (it.enabled && !it.enabled()) continue;
      const d = Math.hypot(it.x - p.x, it.y - p.y);
      if (d > it.radius) continue;
      const pri = it.priority ?? 0;
      if (pri > bestPri || (pri === bestPri && d < bestDist)) {
        best = it;
        bestPri = pri;
        bestDist = d;
      }
    }
    return best;
  };

  return {
    get focused() {
      return focused;
    },
    add(i: Interactable) {
      items.add(i);
      return () => {
        items.delete(i);
        if (focused === i) focused = null;
      };
    },
    update() {
      focused = pick();
      const text = ensureLabel();
      if (focused) {
        const alt = focused.altPrompt && focused.onAlt ? `   [X] ${focused.altPrompt}` : '';
        const str = `[E] ${focused.prompt}${alt}`;
        if (text.text !== str) text.setText(str);
        text.setPosition(focused.x, focused.y - focused.radius * 0.35 - 40);
        text.setAlpha(Math.min(1, text.alpha + 0.15));
        if (ctx.input.pressed('interact')) {
          ctx.input.consume('interact');
          focused.onInteract();
        } else if (focused.onAlt && ctx.input.pressed('salvage')) {
          ctx.input.consume('salvage');
          focused.onAlt();
        }
      } else {
        text.setAlpha(Math.max(0, text.alpha - 0.15));
      }
    },
    destroy() {
      items.clear();
      focused = null;
      label?.destroy();
      label = null;
    },
  };
}
